import React from 'react'
import { any, browserRouter, componentDidCatch, fromJSX, loadingAsyncState, stateful, Updater, Widget } from 'widgets-for-react'
import { genericCmsPageWidget, homePageWidget } from './cmsPage/cmsPageWidget'
import { CookiebarState, cookiebarStateUpdaters } from './cookiebar/cookiebarState'
import { cookiebarWidget } from './cookiebar/cookiebarWidget'
import { courseDateScheduleWidget } from './courseDateSchedule/courseSearchWidget'
import { courseSearchWidget } from './courseSearch/courseSearchWidget'
import { footerWidget } from './footer/footerWidget'
import { headerWidget } from './header/headerWidget'
import { knowledgeSearchWidget } from './knowledgeSearch/knowledgeSearchWidget'
import { notFoundWidget } from './notFound/notFoundWidget'
import { RootLayout } from './rootLayout'
import { initialRootState, RootProps, RootState, rootStateUpdaters } from './rootState'
import { isDifferentRoute, routesWidget } from './routes/routesWidget'
import { searchWidget } from './search/searchWidget'
import { viewListenerWidget } from './viewListener/viewListenerWidget'

import i18next from 'i18next'
import { SpotlerViewEvent } from '../utils/sqzl'
import { actualitySearchWidget } from './actualitySearch/actualitySearchWidget'
import { educationFormWidget } from './educationForm/educationFormWidget'
import { emptyWidget } from './empty/emptyWidget'
import { EventFormWidget } from './eventForm/eventForm.widget'
import { jqueryWidget } from './jquery/jqueryWidget'
import { magazinesOverviewWidget } from './magazinesOverview/magazinesOverviewWidget'
import { mailPlusFormWidget } from './mailPlusForm/mailPlusFormWidget'
import { NotFoundLayout } from './notFound/notFoundLayout'
import { opleidingenOverviewWidget } from './opleidingenOverview/opleidingenOverviewWidget'
import { fetchRedirect } from './redirects/redirectApi'
import { redirectWidget } from './redirects/redirectWidget'

export const rootWidget = (props: RootProps) =>
  browserRouter()(
    stateful()<RootState>(state =>
      fromJSX<Updater<RootState>>(setState => {
        const setCookiebarState = (updater: Updater<CookiebarState>) =>
          setState(rootStateUpdaters.updateCookiebarState(updater))
        return any<Updater<RootState>>([
          redirectWidget(state.redirect).map(rootStateUpdaters.updateRedirectState),
          routesWidget(),
          jqueryWidget(state.jquery).map(rootStateUpdaters.updateJqueryState),
          viewListenerWidget.map(rootStateUpdaters.updateViewListenerState),
          cookiebarWidget(state.cookiebarState).map(rootStateUpdaters.updateCookiebarState),
          headerWidget(state.view)(state.headerState).map(rootStateUpdaters.updateHeaderState),
          page(setCookiebarState)(state).map(rootStateUpdaters.updateFirstTimeLoadingState).wrapHTML(element => (
            <RootLayout
              kind={state.page.kind}
              extraClasses={
                state.page.kind === 'cmsPage' &&
                state.page.pageState.pageWithBlocksState.kind === 'loaded'
                  ? `page--${state.page.pageState.pageWithBlocksState.value.kind}`
                  : ''
              }
            >
              {element}
            </RootLayout>
          )),
          footerWidget(
            () =>
              setCookiebarState(cookiebarStateUpdaters.cookiebarVisibleUpdater),
            state.page.kind !== 'notFound' &&
              !(
                state.page.kind == 'cmsPage' &&
                state.page.pageState.pageWithBlocksState.kind == 'failed'
              ) &&
              state.page.kind === 'cmsPage' &&
              state.page.pageState.pageWithBlocksState.kind === 'loaded' &&
              state.page.pageState.pageWithBlocksState.value.kind !== 'home' &&
              state.page.params.slug !== 'lidmaatschap'
          )(state.footerState, state.jquery).map(
            rootStateUpdaters.updateFooterState
          )
        ]).run(updater => setState(updater))
      }).map((updater: Updater<RootState>) => (state: RootState): RootState => {
        const newState = updater(state)

        const routeChanged = isDifferentRoute[newState.page.kind](state, newState)
        const initialPageLoaded = state.firstTimeLoading && !newState.firstTimeLoading

        if (routeChanged) {
          window.scrollTo(0, 0)
        }

        if (routeChanged || initialPageLoaded) {
          SpotlerViewEvent()
        }

        const redirectData = routeChanged
          ? loadingAsyncState(() => fetchRedirect(window.location.pathname))
          : newState.redirect.redirect

        return {
          ...newState,
          redirect: {
            ...newState.redirect,
            redirect: redirectData
          }
        }
      })
    )(initialRootState(props))
  )

const page = (setCookiebarState: (_: Updater<CookiebarState>) => void) => (
  currentState: RootState
) =>
  componentDidCatch(
    currentState.page.kind,
    pageSwitch(setCookiebarState)(currentState),
    error => fromJSX(() => <>{i18next.t('errorPage:generalErrorMessage')}</>)
  )

const pageSwitch = (
  setCookiebarState: (_: Updater<CookiebarState>) => void
) => (currentState: RootState): Widget<Updater<RootState>> => {
  switch (currentState.page.kind) {
    case 'mailPlusForm':
      return mailPlusFormWidget(
        currentState.page.pageState,
        currentState.jquery,
        () => <NotFoundLayout />,
        true
      ).map(rootStateUpdaters.updateMailPlusFormPageState)
    case 'educationForm':
      return educationFormWidget(currentState.page.params, currentState.page.pageState).map(
        rootStateUpdaters.updateEducationFormPageState
      )
    case 'eventForm':
      return EventFormWidget(currentState.page.params, currentState.page.pageState).map(
      rootStateUpdaters.updateEventFormPageState
    )
    case 'courseDateSchedule':
      return courseDateScheduleWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateCourseDateSchedulePageState
      )
    case 'courseSearch':
      return courseSearchWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateCourseSearchPageState
      )
    case 'actualitySearch':
      return actualitySearchWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateActualitySearchPageState
      )
    case 'knowledgeSearch':
      return knowledgeSearchWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateKnowledgeSearchPageState
      )
    case 'search':
      return searchWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateSearchPageState
      )
    case 'opleidingenOverview':
      return opleidingenOverviewWidget(setCookiebarState, currentState.page.pageState).map(
        rootStateUpdaters.updateOpleidingenOverviewPageState
      )
    case 'magazinesOverviewPage':
      return magazinesOverviewWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateMagazinesOverviewPageState
      )
    case 'notFound':
      return notFoundWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateNotFoundPageState
      )
    case 'empty':
      return emptyWidget(currentState.page.pageState).map(
        rootStateUpdaters.updateEmptyState
      )
    case 'home':
      return homePageWidget(
        setCookiebarState,
        currentState.page.params,
        currentState.firstTimeLoading
      )(currentState.page.pageState).map(rootStateUpdaters.updateHomePageState)
    case 'previewPage':
      return genericCmsPageWidget(
        setCookiebarState,
        currentState.page.params,
        currentState.firstTimeLoading
      )(currentState.page.pageState).map(rootStateUpdaters.updatePreviewPageState)
    case 'cmsPage':
      return genericCmsPageWidget(
        setCookiebarState,
        currentState.page.params,
        currentState.firstTimeLoading
      )(currentState.page.pageState).map(rootStateUpdaters.updateCmsPageState)
  }
}
